import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './common/Modal';

interface CreateGameModalProps {
  onClose: () => void;
  onCreateGame: (gameData: any) => void;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
}

const CreateGameModal: React.FC<CreateGameModalProps> = ({ onClose, onCreateGame, user }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    maxPlayers: 5,
    decks: 1,
    dealtCards: 4,
    cardsPerDeal: 2,
    gamePoints: 100,
    type: 'public' as 'public' | 'private' | 'friends',
    gameVariant: 'lower_green' as 'lower_green' | 'raub' | 'sedmica',
    gameMode: 'friendly' as 'friendly' | 'betting',
    entryFee: 0,
    platformFee: 5,
  });

  // Generate automatic game name
  const generateGameName = () => {
    const hostname = user ? `${user.firstName} ${user.lastName}` : 'Player';
    const variant = formData.gameVariant === 'lower_green' ? 'Pharaon' : formData.gameVariant === 'raub' ? 'Raub' : 'Sedmica';
    const maxPlayers = formData.maxPlayers;
    const mode = formData.gameMode === 'friendly' ? 'Friendly' : 'Betting';

    return `${hostname} - ${variant} - ${maxPlayers} players - ${mode}`;
  };

  // Update name whenever relevant fields change
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      name: generateGameName()
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.gameVariant, formData.maxPlayers, formData.gameMode, user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Set variant-specific defaults
    if (name === 'gameVariant' && value === 'lower_green') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        maxPlayers: 5,
        decks: 1,
        dealtCards: 4,
        cardsPerDeal: 2
      }));
    } else if (name === 'gameVariant' && value === 'raub') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        maxPlayers: 4,
        decks: 1,
        dealtCards: 4,
        cardsPerDeal: 2
      }));
    } else if (name === 'gameVariant' && value === 'sedmica') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        maxPlayers: 4,
        decks: 1,
        dealtCards: 4,
        cardsPerDeal: 2,
        gamePoints: 120,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: name === 'maxPlayers' || name === 'decks' || name === 'dealtCards' || name === 'cardsPerDeal' || name === 'gamePoints' || name === 'entryFee' || name === 'platformFee'
          ? parseInt(value)
          : value
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const gameData = {
      ...formData,
      name: generateGameName()
    };
    onCreateGame(gameData);
  };

  const footer = (
    <>
      <button type="button" className="ui-btn ui-btn-secondary" onClick={onClose}>
        {t('common.cancel')}
      </button>
      <button type="submit" form="create-game-form" className="ui-btn ui-btn-primary">
        {t('game.create')}
      </button>
    </>
  );

  return (
    <Modal title={t('game.modalTitle')} onClose={onClose} maxWidth={600} footer={footer}>
      <form id="create-game-form" onSubmit={handleSubmit}>
        <div className="ui-name-preview">
          <span className="ui-name-preview-label">Generated Game Name</span>
          <div className="ui-name-preview-value">{generateGameName()}</div>
        </div>

        <div className="ui-field">
          <label className="ui-label" htmlFor="gameVariant">{t('game.gameVariant')}</label>
          <select id="gameVariant" name="gameVariant" className="ui-select" value={formData.gameVariant} onChange={handleChange} required>
            <option value="lower_green">{t('game.lowerGreen')} - {t('game.lowerGreenDescription')}</option>
            <option value="raub">{t('game.raub')} - {t('game.raubDescription')}</option>
            <option value="sedmica">Sedmica - Trick-taking card game (2 or 4 players)</option>
          </select>
        </div>

        {/* Pharaon-specific fields */}
        {formData.gameVariant === 'lower_green' && (
          <>
            <div className="ui-field">
              <label className="ui-label" htmlFor="maxPlayers">{t('game.maxPlayers')}</label>
              <select id="maxPlayers" name="maxPlayers" className="ui-select" value={formData.maxPlayers} onChange={handleChange} required>
                {[2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n} players</option>)}
              </select>
            </div>

            <div className="ui-field">
              <label className="ui-label" htmlFor="decks">{t('game.numberOfDecks')}</label>
              <select id="decks" name="decks" className="ui-select" value={formData.decks} onChange={handleChange} required>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <option key={n} value={n}>{n} deck{n > 1 ? 's' : ''}</option>)}
              </select>
            </div>

            <div className="ui-field">
              <label className="ui-label" htmlFor="dealtCards">{t('game.cardsPerPlayer')}</label>
              <select id="dealtCards" name="dealtCards" className="ui-select" value={formData.dealtCards} onChange={handleChange} required>
                {[2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <option key={n} value={n}>{n} cards</option>)}
              </select>
            </div>

            <div className="ui-field">
              <label className="ui-label" htmlFor="cardsPerDeal">{t('game.cardsPerDeal')}</label>
              <select id="cardsPerDeal" name="cardsPerDeal" className="ui-select" value={formData.cardsPerDeal} onChange={handleChange} required>
                <option value="2">2 cards</option>
              </select>
            </div>

            <div className="ui-field">
              <label className="ui-label" htmlFor="gamePoints">{t('game.gamePoints')}</label>
              <select id="gamePoints" name="gamePoints" className="ui-select" value={formData.gamePoints} onChange={handleChange} required>
                {[100, 150, 200, 250, 300, 350, 500].map(n => <option key={n} value={n}>{n} points</option>)}
              </select>
            </div>
          </>
        )}

        {/* Raub fixed settings */}
        {formData.gameVariant === 'raub' && (
          <div className="ui-note">
            <h5 className="ui-note-title">Raub Game Settings</h5>
            <div className="ui-note-body">
              <div>Max Players: 4 (fixed)</div>
              <div>Number of Decks: 1 (fixed)</div>
              <div>Cards per Player: 4 (fixed)</div>
              <div>Cards per Deal: 2 (fixed)</div>
              <div>Game Points: {formData.gamePoints} (configurable)</div>
            </div>
          </div>
        )}

        {/* Sedmica settings */}
        {formData.gameVariant === 'sedmica' && (
          <>
            <div className="ui-note">
              <h5 className="ui-note-title">Sedmica Game Settings</h5>
              <div className="ui-note-body">
                <div>Cards per Player: 4 (fixed)</div>
                <div>32-card deck (fixed)</div>
                <div>Aces and Tens = 10 points, last trick = +10</div>
                <div>Total available per deal: 90 points</div>
              </div>
            </div>
            <div className="ui-field">
              <label className="ui-label" htmlFor="maxPlayers">Number of Players</label>
              <select id="maxPlayers" name="maxPlayers" className="ui-select" value={formData.maxPlayers} onChange={handleChange} required>
                <option value="2">2 players</option>
                <option value="4">4 players (partnerships)</option>
              </select>
            </div>
            <div className="ui-field">
              <label className="ui-label" htmlFor="gamePoints">Target Score</label>
              <select id="gamePoints" name="gamePoints" className="ui-select" value={formData.gamePoints} onChange={handleChange} required>
                <option value="90">90 points (1 deal)</option>
                <option value="120">120 points (standard)</option>
                <option value="180">180 points (long game)</option>
                <option value="270">270 points (marathon)</option>
              </select>
            </div>
          </>
        )}

        <div className="ui-field">
          <label className="ui-label" htmlFor="type">{t('game.gameType')}</label>
          <select id="type" name="type" className="ui-select" value={formData.type} onChange={handleChange} required>
            <option value="public">{t('game.public')} - {t('game.publicDescription')}</option>
            <option value="private">{t('game.private')} - {t('game.privateDescription')}</option>
            <option value="friends">{t('game.friendsOnly')} - {t('game.friendsOnlyDescription')}</option>
          </select>
        </div>

        <div className="ui-field">
          <label className="ui-label" htmlFor="gameMode">{t('game.gameMode')}</label>
          <select id="gameMode" name="gameMode" className="ui-select" value={formData.gameMode} onChange={handleChange} required>
            <option value="friendly">{t('game.friendly')} - {t('game.friendlyDescription')}</option>
            <option value="betting">{t('game.betting')} - {t('game.bettingDescription')}</option>
          </select>
        </div>

        {formData.gameMode === 'betting' && (
          <>
            <div className="ui-field">
              <label className="ui-label" htmlFor="entryFee">{t('game.entryFee')} (ZBIO)</label>
              <input type="number" id="entryFee" name="entryFee" className="ui-input" min="0" value={formData.entryFee} onChange={handleChange} required />
            </div>
            <div className="ui-field">
              <label className="ui-label" htmlFor="platformFee">{t('game.platformFee')} (%)</label>
              <input type="number" id="platformFee" name="platformFee" className="ui-input" min="0" max="20" value={formData.platformFee} onChange={handleChange} disabled required />
            </div>
          </>
        )}
      </form>
    </Modal>
  );
};

export default CreateGameModal;
